<template>
  <div class="home-layout">
    <!-- 顶栏 -->
    <header class="home-header">
      <div class="header-left">
        <span class="logo">智能售后远程服务系统</span>
      </div>
      <div class="header-right">
        <el-button v-if="isAdmin" type="primary" plain size="small" @click="openUserMgr">用户管理</el-button>
        <span class="user-info">{{ userInfo?.Email || '' }}</span>
        <el-button type="danger" text @click="handleLogout">退出登录</el-button>
      </div>
    </header>

    <!-- 中间内容区 -->
    <main class="home-main">
      <!-- 左侧面板：管理员才显示厂商列表 -->
      <div v-if="isAdmin" class="left-panel">
        <div class="panel-header">
          <span class="panel-title">厂商列表</span>
        </div>
        <div class="nav-list">
          <div
            v-for="item in manufacturerList"
            :key="item.manufacturerID"
            class="nav-item"
            :class="{ active: selectedManufacturerID === item.manufacturerID }"
            @click="selectManufacturer(item)"
          >
            <div class="nav-name">{{ item.manufacturer }}</div>
            <div class="nav-meta">
              <span class="mfr-id">ID: {{ item.manufacturerID }}</span>
              <span class="device-count">{{ item.deviceCount }} 台设备</span>
              <span class="nav-arrow">›</span>
            </div>
          </div>
          <div v-if="manufacturerList.length === 0" class="empty-tip">暂无厂商数据</div>
        </div>
      </div>

      <!-- 右侧：设备列表 -->
      <div class="right-panel">
        <div class="panel-header">
          <span class="panel-title">{{ panelTitle }}</span>
        </div>
        <el-table
          :data="deviceList"
          stripe
          border
          row-key="ID"
          style="width: 100%"
          height="100%"
          :empty-text="isAdmin ? '请在左侧选择厂商查看设备' : '暂无设备数据'"
          @expand-change="handleExpand"
        >
          <el-table-column type="expand">
            <template #default="{ row }">
              <div class="expand-content">
                <div class="expand-title">上报日志记录</div>
                <el-table :data="row._logs || []" size="small" border stripe empty-text="暂无日志记录" max-height="400" style="width:100%">
                  <el-table-column type="index" label="#" width="50" align="center" />
                  <el-table-column prop="errorCode" label="错误代码" width="140" align="center">
                    <template #default="{ row: logRow }">
                      <el-tag v-if="logRow.errorCode" type="danger" size="small">{{ logRow.errorCode }}</el-tag>
                      <span v-else class="expand-normal">无</span>
                    </template>
                  </el-table-column>
                  <el-table-column prop="deviceCode" label="设备类型" width="100" align="center">
                    <template #default="{ row: logRow }">
                      <el-tag v-if="logRow.deviceCode === '1'" type="warning" size="small">高压</el-tag>
                      <el-tag v-else-if="logRow.deviceCode === '2'" type="info" size="small">平板</el-tag>
                      <span v-else class="expand-normal">无</span>
                    </template>
                  </el-table-column>
                  <el-table-column prop="message" label="错误日志" min-width="300">
                    <template #default="{ row: logRow }">
                      <pre v-if="logRow.message" class="expand-log-inline">{{ logRow.message }}</pre>
                      <span v-else class="expand-normal">无</span>
                    </template>
                  </el-table-column>
                  <el-table-column prop="reportedAt" label="上报时间" width="180" align="center" />
                </el-table>
              </div>
            </template>
          </el-table-column>
          <el-table-column type="index" label="序号" width="60" align="center" />
          <el-table-column prop="deviceSN" label="设备序列号" min-width="200" />
          <el-table-column prop="customer" label="客户名称" min-width="160" show-overflow-tooltip />
          <el-table-column prop="errorCode" label="错误代码" width="130" align="center">
            <template #default="{ row }">
              <el-tag v-if="row.errorCode" type="danger">{{ row.errorCode }}</el-tag>
              <el-tag v-else type="success">正常</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="deviceCode" label="设备类型" width="90" align="center">
            <template #default="{ row }">
              <el-tag v-if="row.deviceCode === '1'" type="warning">{{ row.deviceCode === '1' ? '高压' : row.deviceCode }}</el-tag>
              <el-tag v-else-if="row.deviceCode === '2'" type="info">{{ row.deviceCode === '2' ? '平板' : row.deviceCode }}</el-tag>
              <span v-else>-</span>
            </template>
          </el-table-column>
          <el-table-column prop="lastTime" label="最后上报时间" width="180" align="center" />
        </el-table>
      </div>
    </main>

    <!-- 底栏 -->
    <footer class="home-footer">
      <span>&copy; 2026 DR RemoteCheck. All rights reserved.</span>
    </footer>

    <!-- ========== 用户管理对话框 ========== -->
    <el-dialog v-model="userMgrVisible" title="用户管理" width="1060px" destroy-on-close>
      <div class="user-mgr-toolbar">
        <span class="user-mgr-count">共 {{ userList.length }} 个账号</span>
        <el-button type="primary" @click="openCreateDialog">+ 新增用户</el-button>
      </div>
      <el-table
        :data="userList"
        stripe
        border
        style="width:100%;margin-top:12px"
        max-height="500"
      >
        <el-table-column type="index" label="#" width="50" align="center" />
        <el-table-column prop="email" label="邮箱账号" min-width="180" show-overflow-tooltip />
        <el-table-column prop="nickName" label="昵称" width="110" show-overflow-tooltip />
        <el-table-column prop="manufacturer" label="关联厂商" min-width="150" show-overflow-tooltip>
          <template #default="{ row }">
            <span v-if="row.manufacturer">{{ row.manufacturerID }} - {{ row.manufacturer }}</span>
            <span v-else class="text-placeholder">未关联</span>
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="70" align="center">
          <template #default="{ row }">
            <el-tag :type="row.status === 1 ? 'success' : 'danger'" size="small">
              {{ row.status === 1 ? '启用' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="createdAt" label="创建时间" width="155" align="center" />
        <el-table-column label="操作" width="205" align="center">
          <template #default="{ row }">
            <div class="action-btns">
              <el-tooltip content="编辑用户" placement="top">
                <el-button circle size="small" type="info" @click="openEditUser(row)">
                  <el-icon><Edit /></el-icon>
                </el-button>
              </el-tooltip>
              <el-tooltip :content="row.status === 1 ? '禁用账号' : '启用账号'" placement="top">
                <el-button
                  circle
                  size="small"
                  :type="row.status === 1 ? 'warning' : 'success'"
                  @click="toggleStatus(row)"
                >
                  <el-icon><Lock v-if="row.status === 1" /><Unlock v-else /></el-icon>
                </el-button>
              </el-tooltip>
              <el-tooltip content="重置密码" placement="top">
                <el-button circle size="small" type="primary" @click="openResetPwd(row)">
                  <el-icon><Key /></el-icon>
                </el-button>
              </el-tooltip>
              <el-tooltip content="删除用户" placement="top">
                <el-button circle size="small" type="danger" @click="handleDelete(row)">
                  <el-icon><Delete /></el-icon>
                </el-button>
              </el-tooltip>
            </div>
          </template>
        </el-table-column>
      </el-table>
    </el-dialog>

    <!-- 新增用户对话框 -->
    <el-dialog v-model="createVisible" title="新增用户" width="420px" destroy-on-close>
      <el-form :model="createForm" label-width="80px">
        <el-form-item label="邮箱" required>
          <el-input v-model="createForm.email" placeholder="请输入登录邮箱" />
        </el-form-item>
        <el-form-item label="密码" required>
          <el-input v-model="createForm.password" type="password" placeholder="请输入初始密码" show-password />
        </el-form-item>
        <el-form-item label="昵称">
          <el-input v-model="createForm.nickName" placeholder="不填则使用邮箱" />
        </el-form-item>
        <el-form-item label="关联厂商">
          <el-select
            v-model="createForm.manufacturerID"
            placeholder="请选择或新建厂商"
            clearable
            filterable
            style="width:100%"
          >
            <el-option
              v-for="opt in manufacturerOptions"
              :key="opt.id"
              :label="opt.id + ' - ' + opt.name"
              :value="opt.id"
            />
            <template #footer>
              <div v-if="!newMfrInputVisible" class="select-footer-btn" @click="showNewMfrInput">
                <el-icon><Plus /></el-icon> 新建厂商
              </div>
              <div v-else class="select-footer-input">
                <el-input
                  ref="newMfrInputRef"
                  v-model="newMfrName"
                  placeholder="输入厂商名称"
                  size="small"
                  @keyup.enter="submitNewMfr"
                  @keyup.esc="newMfrInputVisible = false"
                />
                <el-button size="small" type="primary" :loading="newMfrLoading" @click="submitNewMfr">确认</el-button>
                <el-button size="small" @click="newMfrInputVisible = false">取消</el-button>
              </div>
            </template>
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="createVisible = false">取消</el-button>
        <el-button type="primary" :loading="createLoading" @click="handleCreate">确认创建</el-button>
      </template>
    </el-dialog>

    <!-- 重置密码对话框 -->
    <el-dialog v-model="resetPwdVisible" title="重置密码" width="380px" destroy-on-close>
      <el-form label-width="80px">
        <el-form-item label="新密码" required>
          <el-input v-model="resetPwdForm.password" type="password" placeholder="请输入新密码" show-password />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="resetPwdVisible = false">取消</el-button>
        <el-button type="primary" :loading="resetPwdLoading" @click="handleResetPwd">确认重置</el-button>
      </template>
    </el-dialog>

    <!-- 编辑用户对话框 -->
    <el-dialog v-model="editVisible" title="编辑用户" width="420px" destroy-on-close>
      <el-form :model="editForm" label-width="80px">
        <el-form-item label="邮箱">
          <el-input v-model="editForm.email" disabled />
        </el-form-item>
        <el-form-item label="昵称">
          <el-input v-model="editForm.nickName" placeholder="请输入昵称" />
        </el-form-item>
        <el-form-item label="关联厂商">
          <el-select
            v-model="editForm.manufacturerID"
            placeholder="请选择或新建厂商"
            clearable
            filterable
            style="width:100%"
          >
            <el-option
              v-for="opt in manufacturerOptions"
              :key="opt.id"
              :label="opt.id + ' - ' + opt.name"
              :value="opt.id"
            />
            <template #footer>
              <div v-if="!newMfrInputVisible2" class="select-footer-btn" @click="showNewMfrInput2">
                <el-icon><Plus /></el-icon> 新建厂商
              </div>
              <div v-else class="select-footer-input">
                <el-input
                  ref="newMfrInputRef2"
                  v-model="newMfrName2"
                  placeholder="输入厂商名称"
                  size="small"
                  @keyup.enter="submitNewMfr2"
                  @keyup.esc="newMfrInputVisible2 = false"
                />
                <el-button size="small" type="primary" :loading="newMfrLoading2" @click="submitNewMfr2">确认</el-button>
                <el-button size="small" @click="newMfrInputVisible2 = false">取消</el-button>
              </div>
            </template>
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="editVisible = false">取消</el-button>
        <el-button type="primary" :loading="editLoading" @click="handleEdit">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Lock, Unlock, Key, Delete, Plus, Edit } from '@element-plus/icons-vue'
import { PublicStore } from '@/tools/pinia/store'
import { postRequest } from '@/tools/axios/http'

const router = useRouter()
const publicData = PublicStore()

const userInfo = computed(() => publicData.UserInfo)
const isAdmin = computed(() => userInfo.value?.UserType === 0)

const selectedManufacturerID = ref(null)
const selectedManufacturerName = ref('')
const manufacturerList = ref([])
const deviceList = ref([])

const panelTitle = computed(() => {
  if (!isAdmin.value) return '设备列表'
  if (selectedManufacturerName.value) return selectedManufacturerName.value + ' - 设备列表'
  return '请选择厂商查看设备'
})

// ===== 厂商 & 设备 =====
const fetchManufacturers = async () => {
  const res = await postRequest('/dental/device/manufacturers', {})
  if (res.code === 200) manufacturerList.value = res.data || []
}

const selectManufacturer = (item) => {
  selectedManufacturerID.value = item.manufacturerID
  selectedManufacturerName.value = item.manufacturer
  fetchDevices(item.manufacturerID)
}

const fetchDevices = async (manufacturerID) => {
  const res = await postRequest('/dental/device/list', { manufacturerID })
  if (res.code === 200) deviceList.value = res.data || []
}

const handleExpand = async (row, expandedRows) => {
  if (!expandedRows.includes(row)) return
  if (row._logs) return
  const res = await postRequest('/dental/device/logs', { deviceID: row.ID })
  if (res.code === 200) row._logs = res.data || []
}

// ===== 用户管理 =====
const userMgrVisible = ref(false)
const userList = ref([])
const manufacturerOptions = ref([])

const openUserMgr = async () => {
  userMgrVisible.value = true
  await fetchUserList()
  await fetchManufacturerOptions()
}

const fetchUserList = async () => {
  const res = await postRequest('/dental/user/list', {})
  if (res.code === 200) userList.value = res.data || []
}

const fetchManufacturerOptions = async () => {
  const res = await postRequest('/dental/user/manufacturerOptions', {})
  if (res.code === 200) manufacturerOptions.value = (res.data || []).map(v => ({ ...v, id: Number(v.id) }))
}

// 新增用户
const createVisible = ref(false)
const createLoading = ref(false)
const createForm = ref({ email: '', password: '', nickName: '', manufacturerID: null })

// 新建厂商（内联）
const newMfrInputVisible = ref(false)
const newMfrName = ref('')
const newMfrLoading = ref(false)
const newMfrInputRef = ref(null)

const showNewMfrInput = async () => {
  newMfrInputVisible.value = true
  newMfrName.value = ''
  await nextTick()
  newMfrInputRef.value?.focus()
}

const submitNewMfr = async () => {
  if (!newMfrName.value.trim()) return
  newMfrLoading.value = true
  const res = await postRequest('/dental/user/createManufacturer', { name: newMfrName.value.trim() })
  newMfrLoading.value = false
  if (res.code === 200) {
    const created = res.data
    // 刷新下拉列表并自动选中新厂商
    await fetchManufacturerOptions()
    createForm.value.manufacturerID = created.id
    ElMessage.success(`厂商「${created.name}」创建成功`)
    newMfrInputVisible.value = false
    newMfrName.value = ''
  } else {
    ElMessage.error('创建失败')
  }
}

const openCreateDialog = () => {
  createForm.value = { email: '', password: '', nickName: '', manufacturerID: null }
  newMfrInputVisible.value = false
  newMfrName.value = ''
  createVisible.value = true
}

const handleCreate = async () => {
  if (!createForm.value.email || !createForm.value.password) {
    ElMessage.warning('邮箱和密码不能为空')
    return
  }
  createLoading.value = true
  const res = await postRequest('/dental/user/create', {
    email: createForm.value.email,
    password: createForm.value.password,
    nickName: createForm.value.nickName,
    manufacturerID: createForm.value.manufacturerID || 0,
  })
  createLoading.value = false
  if (res.code === 200) {
    ElMessage.success('创建成功')
    createVisible.value = false
    fetchUserList()
  } else if (res.code === 10001) {
    ElMessage.error('该邮箱已存在')
  } else {
    ElMessage.error('创建失败')
  }
}

// 启用/禁用
const toggleStatus = async (row) => {
  const newStatus = row.status === 1 ? 0 : 1
  const label = newStatus === 1 ? '启用' : '禁用'
  await ElMessageBox.confirm(`确定要${label}用户 ${row.email} 吗？`, '提示', { type: 'warning' })
  const res = await postRequest('/dental/user/updateStatus', { userID: String(row.userID), status: newStatus })
  if (res.code === 200) {
    ElMessage.success(`${label}成功`)
    row.status = newStatus
  } else {
    ElMessage.error('操作失败')
  }
}

// 编辑用户
const editVisible = ref(false)
const editLoading = ref(false)
const editForm = ref({ userID: null, email: '', nickName: '', manufacturerID: null })

// 编辑专用：新建厂商
const newMfrInputVisible2 = ref(false)
const newMfrName2 = ref('')
const newMfrLoading2 = ref(false)
const newMfrInputRef2 = ref(null)

const showNewMfrInput2 = async () => {
  newMfrInputVisible2.value = true
  newMfrName2.value = ''
  await nextTick()
  newMfrInputRef2.value?.focus()
}

const submitNewMfr2 = async () => {
  if (!newMfrName2.value.trim()) return
  newMfrLoading2.value = true
  const res = await postRequest('/dental/user/createManufacturer', { name: newMfrName2.value.trim() })
  newMfrLoading2.value = false
  if (res.code === 200) {
    const created = res.data
    await fetchManufacturerOptions()
    editForm.value.manufacturerID = created.id
    ElMessage.success(`厂商「${created.name}」创建成功`)
    newMfrInputVisible2.value = false
    newMfrName2.value = ''
  } else {
    ElMessage.error('创建失败')
  }
}

const openEditUser = async (row) => {
  await fetchManufacturerOptions()
  editForm.value = {
    userID: row.userID,
    email: row.email,
    nickName: row.nickName,
    manufacturerID: row.manufacturerID ? Number(row.manufacturerID) : null,
  }
  newMfrInputVisible2.value = false
  newMfrName2.value = ''
  editVisible.value = true
}

const handleEdit = async () => {
  editLoading.value = true
  const res = await postRequest('/dental/user/update', {
    userID: String(editForm.value.userID),
    nickName: editForm.value.nickName,
    manufacturerID: editForm.value.manufacturerID || 0,
  })
  editLoading.value = false
  if (res.code === 200) {
    ElMessage.success('保存成功')
    editVisible.value = false
    fetchUserList()
  } else {
    ElMessage.error('保存失败')
  }
}

// 重置密码
const resetPwdVisible = ref(false)
const resetPwdLoading = ref(false)
const resetPwdForm = ref({ userID: null, password: '' })

const openResetPwd = (row) => {
  resetPwdForm.value = { userID: row.userID, password: '' }
  resetPwdVisible.value = true
}

const handleResetPwd = async () => {
  if (!resetPwdForm.value.password) {
    ElMessage.warning('密码不能为空')
    return
  }
  resetPwdLoading.value = true
  const res = await postRequest('/dental/user/resetPassword', {
    userID: String(resetPwdForm.value.userID),
    password: resetPwdForm.value.password,
  })
  resetPwdLoading.value = false
  if (res.code === 200) {
    ElMessage.success('密码重置成功')
    resetPwdVisible.value = false
  } else {
    ElMessage.error('重置失败')
  }
}

// 删除用户
const handleDelete = async (row) => {
  await ElMessageBox.confirm(`确定要删除用户 ${row.email} 吗？此操作不可恢复！`, '警告', { type: 'error' })
  const res = await postRequest('/dental/user/delete', { userID: String(row.userID) })
  if (res.code === 200) {
    ElMessage.success('删除成功')
    fetchUserList()
  } else {
    ElMessage.error('删除失败')
  }
}

// ===== 初始化 =====
onMounted(() => {
  if (isAdmin.value) {
    fetchManufacturers()
  } else {
    fetchDevices(0)
  }
})

const handleLogout = () => {
  publicData.changeAccessToken('')
  publicData.changeRefreshToken('')
  router.replace('/login')
}
</script>

<style scoped>
.home-layout {
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100%;
  overflow: hidden;
}

.home-header {
  flex-shrink: 0;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  background-color: #409eff;
  color: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.header-left .logo {
  font-size: 20px;
  font-weight: bold;
  letter-spacing: 1px;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-info {
  font-size: 14px;
}

.home-main {
  flex: 1;
  overflow: hidden;
  display: flex;
  gap: 16px;
  padding: 16px;
  background-color: #f5f7fa;
}

.left-panel {
  width: 280px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.right-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.panel-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #ebeef5;
  background: #fafbfc;
}

.panel-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}

.nav-list {
  flex: 1;
  overflow-y: auto;
}

.nav-item {
  padding: 12px 16px;
  cursor: pointer;
  border-bottom: 1px solid #f2f3f5;
  transition: background 0.15s;
}

.nav-item:hover { background: #ecf5ff; }

.nav-item.active {
  background: #e6f0ff;
  border-left: 3px solid #409eff;
  padding-left: 13px;
}

.nav-name {
  font-size: 14px;
  font-weight: 500;
  color: #303133;
  margin-bottom: 4px;
}

.nav-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.mfr-id { font-size: 11px; color: #909399; }
.device-count { font-size: 12px; color: #909399; }
.nav-arrow { font-size: 18px; color: #c0c4cc; font-weight: bold; }

.empty-tip {
  padding: 40px 0;
  text-align: center;
  color: #909399;
  font-size: 14px;
}

.expand-content { padding: 12px 16px; overflow: visible; }
.expand-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 10px;
}
.expand-normal { color: #909399; font-size: 13px; }
.expand-log-inline {
  margin: 0;
  padding: 6px 10px;
  background-color: #1e1e1e;
  color: #d4d4d4;
  border-radius: 4px;
  font-size: 12px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 200px;
  overflow: auto;
}

.home-footer {
  flex-shrink: 0;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #545c64;
  color: #dcdfe6;
  font-size: 12px;
}

.user-mgr-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.user-mgr-count {
  font-size: 13px;
  color: #909399;
}

.action-btns {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.text-placeholder {
  color: #c0c4cc;
  font-size: 13px;
}

.select-footer-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  font-size: 13px;
  color: #409eff;
  cursor: pointer;
  border-top: 1px solid #ebeef5;
}

.select-footer-btn:hover {
  background: #ecf5ff;
}

.select-footer-input {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-top: 1px solid #ebeef5;
}
</style>
